export type EmittedEvent<P = any, T = unknown> = {
  type: string;
  target: T;
  payload: P;
};

export type EmittedEventObject = {
  [index: string]: EmittedEvent;
};

export type EventSubscriber = {
  fired?: boolean;
  immediate?: boolean;
  once?: boolean;
  callback: (event: EmittedEvent) => void;
};

export type EventConfig = {
  immediate?: boolean;
  once?: boolean;
}

export type EventListeners = {
  [key: string]: EventSubscriber[];
}

export abstract class EventEmitter {
  private _eventListeners: EventListeners = {};

  private _firedEvents: EmittedEventObject = {};

  public emit(type: string, payload?: unknown) {
    const event = this.makeEvent(type, payload);

    this._firedEvents[type] = event;

    if (!(event.type in this._eventListeners)) {
      return;
    }
    const subscribers = this._eventListeners[event.type].slice();

    for (let i = 0, l = subscribers.length; i < l; i += 1) {
      const subscriber = subscribers[i];

      subscriber.fired = true;
      subscriber.callback.call(this, event);
    }
  }

  on(type: string, callback: (event: EmittedEvent) => void, config: EventConfig = {
    immediate: false,
    once: false,
  }) {
    if (!(type in this._eventListeners)) {
      this._eventListeners[type] = [];
    }
    this._eventListeners[type].push({
      callback,
      ...config,
    });

    const event = this.makeEvent(type);

    if (this._firedEvents[type]) {
      callback(this._firedEvents[type]);

      return;
    }

    this._eventListeners[type]
      .slice()
      .filter(subscriber => subscriber.fired && subscriber.immediate)
      .forEach(subscriber => subscriber.callback(event));
  }

  off(type?: string) {
    if (type) {
      this._eventListeners[type] = [];
      delete this._firedEvents[type];

      return;
    }
    this._eventListeners = {};
    this._firedEvents = {};
  }

  getSubscribers(type?: string) {
    return type ? (this._eventListeners[type] || []) : (this._eventListeners || {});
  }

  hasSubscribers(type?: string): boolean {
    if (type) { return !!this.getSubscribers(type).length; }

    return Object.values(this.getSubscribers()).some(subscribers => subscribers.length);
  }

  private makeEvent(type: string, payload?: unknown): EmittedEvent {
    return {
      type,
      target: this,
      payload,
    };
  }
}
